import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const STYLE_PROPS = ["fill", "color", "font-size", "stroke", "font"];

const setInlineStyles = (node: Element) => {
  const el = node as HTMLElement;
  if (!el.style) return;
  const styles = getComputedStyle(el);
  for (const prop of STYLE_PROPS) {
    el.style.setProperty(prop, styles.getPropertyValue(prop));
  }
};

export const prepareSvgForCapture = (root: ParentNode) => {
  const svgElements = root.querySelectorAll("svg");
  svgElements.forEach((svgElement) => {
    const bBox = svgElement.getBBox();
    svgElement.setAttribute("width", String(bBox.width));
    svgElement.setAttribute("height", String(bBox.height));
    setInlineStyles(svgElement);
  });
};

export const getReportDomId = (
  socialId: string,
  suffix: "Header" | "Chart"
): string => `${socialId.charAt(0).toLowerCase()}${socialId.slice(1)}${suffix}`;

export const SUMMARY_REPORT_SECTION_IDS = [
  "summaryReportHeader",
  "summaryReportOverview",
  "summaryReportFollowers",
  "summaryReportInteractions",
  "summaryReportImpressions",
  "summaryReportPublications",
  "summaryReportPosts",
] as const;

export const collectSummaryReportSections = (): PdfSection[] =>
  SUMMARY_REPORT_SECTION_IDS.map((id) => document.getElementById(id))
    .filter((element): element is HTMLElement => Boolean(element))
    .map((element) => ({ element }));

const SUMMARY_CAPTURE_EVENT = "summary-capture-change";

const waitForSummaryCharts = async (root: HTMLElement, attempts = 12) => {
  for (let index = 0; index < attempts; index += 1) {
    const canvases = Array.from(root.querySelectorAll("canvas"));
    const ready =
      canvases.length > 0 &&
      canvases.every((canvas) => canvas.width > 0 && canvas.height > 0);
    if (ready) return;
    await new Promise((resolve) => setTimeout(resolve, 120));
    window.dispatchEvent(new Event("resize"));
  }
};

export const setSummaryCaptureMode = async (enabled: boolean) => {
  const root = document.getElementById("summaryReportRoot");
  if (!root) return;

  root.classList.toggle("summary-analytics--capturing", enabled);
  document.body.classList.toggle("summary-capture-active", enabled);
  window.dispatchEvent(
    new CustomEvent(SUMMARY_CAPTURE_EVENT, { detail: { enabled } })
  );

  if (enabled) {
    window.dispatchEvent(new Event("resize"));
    await new Promise((resolve) => setTimeout(resolve, 350));
    window.dispatchEvent(new Event("resize"));
    await waitForSummaryCharts(root);
    await new Promise((resolve) => setTimeout(resolve, 200));
  }
};

export const captureElement = async (
  element: HTMLElement,
  scale = 2
): Promise<HTMLCanvasElement> =>
  html2canvas(element, {
    useCORS: true,
    scale,
    backgroundColor: "#ffffff",
    onclone: (clonedDoc) => prepareSvgForCapture(clonedDoc.body),
  });

export interface PdfSection {
  element: HTMLElement;
}

export const downloadPdfFromSections = async (
  sections: PdfSection[],
  filename: string
) => {
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const maxWidth = pageWidth - margin * 2;
  const maxHeight = pageHeight - margin * 2;

  for (let index = 0; index < sections.length; index++) {
    const canvas = await captureElement(sections[index].element);
    const imgData = canvas.toDataURL("image/png");
    const ratio = canvas.width / canvas.height;
    let renderWidth = maxWidth;
    let renderHeight = renderWidth / ratio;

    if (renderHeight > maxHeight) {
      renderHeight = maxHeight;
      renderWidth = renderHeight * ratio;
    }

    const offsetX = margin + (maxWidth - renderWidth) / 2;
    const offsetY = margin + (maxHeight - renderHeight) / 2;

    if (index > 0) pdf.addPage();
    pdf.addImage(imgData, "PNG", offsetX, offsetY, renderWidth, renderHeight);
  }

  pdf.save(filename);
};
